import { Injectable } from '@angular/core';
import { DealEntry, FundingEntry, LogEntry, LogEntryType, MarketErrorEntry, OrderEntry, WsPrivateConnectEntry, WsPrivateDisconnectEntry, WsPublicConnectEntry, WsPublicDisconnectEntry } from './log-entry';
import { Subject } from 'rxjs';
import { TradeKey } from '../../model/api';
import { BoardId, MarketId } from '../../model/common';
import { Deal, Funding, Order } from '../../model/trade';
import { sortByColumn } from '../../func';
import { isEqual } from 'lodash-es';
import { MarketError } from '../../error/codes';

const LOG_LIMIT = 100_000

@Injectable({
  providedIn: 'root'
})
export class LoggerService {

  private marketLoggers = new Map<MarketId, Logger>()
  private keyLoggers = new Map<number, Logger>()

  getKeyLogger(key: TradeKey) {
    return this.getLogger(this.keyLoggers, key.id)
  }

  getMarketLogger(market: MarketId) {
    return this.getLogger(this.marketLoggers, market)
  }

  deleteKeyLogger(key: TradeKey) {
    this.deleteLogger(this.keyLoggers, key.id)
  }

  deleteMarketLogger(market: MarketId) {
    this.deleteLogger(this.marketLoggers, market)
  }

  deleteAllKeyLoggers() {
    Array.from(this.keyLoggers.keys()).forEach(id => this.deleteLogger(this.keyLoggers, id))
  }

  deleteAllMarketLoggers() {
    Array.from(this.marketLoggers.keys()).forEach(market => this.deleteLogger(this.marketLoggers, market))
  }

  private getLogger<T>(loggersMap: Map<T, Logger>, id: T) {
    let logger = loggersMap.get(id)
    if (!logger) {
      logger = new Logger()
      loggersMap.set(id, logger)
    }
    return logger
  }

  private deleteLogger<T>(loggersMap: Map<T, Logger>, id: T) {
    const logger = loggersMap.get(id)
    if (logger) {
      logger.destroy()
      loggersMap.delete(id)
    }
  }

}


@Injectable({
  providedIn: 'root'
})
export class LogEntryFactory {

  deal(deal: Deal) {
    return new DealEntry(deal)
  }

  funding(funding: Funding) {
    return new FundingEntry(funding)
  }

  order(order: Order) {
    return new OrderEntry(order)
  }

  wsPublicConnect(market: MarketId, board?: BoardId) {
    return new WsPublicConnectEntry(market, board)
  }

  wsPublicDisconnect(market: MarketId, board?: BoardId) {
    return new WsPublicDisconnectEntry(market, board)
  }

  wsPrivateConnect(key: TradeKey, board?: BoardId) {
    return new WsPrivateConnectEntry(key, board)
  }

  wsPrivateDisconnect(key: TradeKey, board?: BoardId) {
    return new WsPrivateDisconnectEntry(key, board)
  }

  marketError(key: TradeKey, error: MarketError) {
    return new MarketErrorEntry(key, error)
  }

}


export class Logger {

  private _items: LogEntry[] = []
  private updateSubject = new Subject<readonly LogEntry[]>()
  readonly update$ = this.updateSubject.asObservable()

  get items(): readonly LogEntry[] {
    return this._items
  }

  /**
   * Добавляет в лог сверху realtime запись
   */
  add(items: LogEntry | LogEntry[]) {
    if (!Array.isArray(items)) {
      items = [items]
    }
    this._items.unshift(...items)
    if (this._items.length > LOG_LIMIT) {
      this._items.length = LOG_LIMIT
    }
    this.updateSubject.next(items)
  }

  /**
   * Добавляет в лог снизу историческую запись
   */
  prepend(items: LogEntry | LogEntry[]) {
    if (this._items.length < LOG_LIMIT) {
      if (!Array.isArray(items)) {
        items = [items]
      }
      this._items.push(...items)
      if (this._items.length > LOG_LIMIT) {
        this._items.length = LOG_LIMIT
      }
      this.updateSubject.next(items)
      sortByColumn(this._items, 'timestamp', false)
    }
  }

  /**
   * Добавляет в лог снизу только уникальную историческую запись
   */
  prependUnique(items: LogEntry | LogEntry[]) {
    if (!Array.isArray(items)) {
      items = [items]
    }
    const unique = items.filter(item1 => {
      return !this._items.find(item2 => {
        if (item1 instanceof DealEntry && item2 instanceof DealEntry) {
          return isEqual(item1.deal, item2.deal)
        }
        if (item1 instanceof OrderEntry && item2 instanceof OrderEntry) {
          return isEqual(item1.order, item2.order)
        }
        if (item1 instanceof FundingEntry && item2 instanceof FundingEntry) {
          return isEqual(item1.funding, item2.funding)
        }
        return false
      })
    })
    this.prepend(unique)
  }

  clear(type?: LogEntryType) {
    if (type) {
      this._items = this._items.filter(item => item.type != type)
    } else {
      this._items = []
    }
  }

  destroy() {
    this.clear()
    this.updateSubject.complete()
  }

}
